import { ChevronLeftIcon } from '@chakra-ui/icons'
import { Flex, Text, Tooltip, VStack } from '@chakra-ui/react'
import { ReactNode } from 'react'
import { useHistory } from 'react-router-dom'
import { useDnd } from '../../hooks/useDnd'
import { useDocument } from '../../hooks/useDocument'

type BackSource =
  | 'clients'
  | 'attendance'
  | 'schedule'
  | 'gallery'
  | 'checkout'
  | 'medicalRecord'
  | 'budget'
  | 'unknown'

export type ArrowBackContext = {
  source?: BackSource
  label: string
  path: string
  description?: string
  useBrowserBack?: boolean
}

interface ArrowBackProps {
  href?: string
  goBack?: boolean
  children?: ReactNode
  isChangeForm?: boolean
  isChangeFormReturning?: (isOpen: boolean) => void
  backContext?: ArrowBackContext
  showDescription?: boolean
}

export const ArrowBack = ({
  href = '',
  goBack = false,
  isChangeForm,
  isChangeFormReturning,
  children,
  backContext,
  showDescription = true
}: ArrowBackProps) => {
  const navigate = useHistory()
  const { setImgSrc, setUploadedFiles } = useDocument()
  const { setListForms } = useDnd()

  const label = backContext?.label
  const description = backContext?.description
  const targetPath = backContext?.path || href
  const shouldUseBrowserBack = backContext?.useBrowserBack ?? goBack

  const handleBack = () => {
    setImgSrc('')
    setUploadedFiles([])
    setListForms([])

    if (isChangeForm) {
      isChangeFormReturning?.(true)
      return
    }

    if (shouldUseBrowserBack) {
      navigate.goBack()
      return
    }

    if (targetPath) {
      navigate.push(targetPath)
    }
  }

  const content = children || (
    <VStack align="flex-start" spacing={0}>
      <Text fontSize="md" fontWeight="bold">
        {label || 'Voltar'}
      </Text>

      {showDescription && description ? (
        <Text fontSize="xs" color="gray.400" lineHeight="short">
          {description}
        </Text>
      ) : null}
    </VStack>
  )

  return (
    <Tooltip
      label={description || label || 'Voltar'}
      aria-label={description || label || 'Voltar'}
      hasArrow
      placement="bottom-start"
      isDisabled={!description && !label}
    >
      <Flex
        color="gray.100"
        _hover={{ color: 'gray.500' }}
        cursor="pointer"
        onClick={handleBack}
        align="center"
        justify="center"
      >
        <ChevronLeftIcon w={10} h={10} />
        {content}
      </Flex>
    </Tooltip>
  )
}
